import { GroupeCreateInput } from "@repo/types";
import { Alert, AlertDescription } from "@repo/ui/components/ui/alert";
import { Button } from "@repo/ui/components/ui/button";
import { Input } from "@repo/ui/components/ui/input";
import { Label } from "@repo/ui/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@repo/ui/components/ui/radio-group";
import { GroupePlanSection } from "@repo/ui/features/groupe/components/shared/groupe-plan-section";
import { useCreateGroupe } from "@repo/ui/hooks/api/groupe";
import { useSubscriptionConfig } from "@repo/ui/hooks/api/settings/use-settings";
import { cn } from "@repo/ui/lib/utils";
import {
  AlertCircle,
  ArrowRight,
  Building2,
  Crown,
  Info,
  Loader2,
  Package,
  Users,
} from "lucide-react";
import { useState } from "react";

type GroupeType = "DENDROMAP" | "PREMIUM";
type PlanType = "BASIC" | "ADVANCED";

interface CreatedGroupData {
  id: number;
  name: string;
  groupe_type: string;
  licence: number;
  plan_type?: string;
  max_parcours_per_month?: number;
  tier_id?: string;
}

interface StepGroupeProps {
  onGroupeCreated: (group: CreatedGroupData) => void;
}

export function StepGroupe({ onGroupeCreated }: StepGroupeProps) {
  const [name, setName] = useState("");
  const [licence, setLicence] = useState<number>(1);
  const [groupeType, setGroupeType] = useState<GroupeType>("DENDROMAP");
  const [selectedPlanType, setSelectedPlanType] = useState<PlanType>("BASIC");
  const [selectedTierId, setSelectedTierId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const { data: subscriptionConfig, isLoading: isLoadingConfig } = useSubscriptionConfig();
  const createGroupe = useCreateGroupe();

  // Auto-select first basic tier when config loads
  const firstBasicTier = subscriptionConfig?.plans?.BASIC?.tiers?.[0];
  if (firstBasicTier && !selectedTierId) {
    setSelectedTierId(firstBasicTier.id);
  }

  const isPremium = groupeType === "PREMIUM";
  const hasPlan = selectedTierId.length > 0;
  const nameError = touched && !name.trim();
  const licenceError = touched && (!licence || licence < 1);
  const planError = touched && isPremium && !hasPlan;

  const canSubmit =
    name.trim().length > 0 &&
    licence >= 1 &&
    (!isPremium || hasPlan);

  const handlePlanSelect = (planType: PlanType, tierId: string) => {
    setSelectedPlanType(planType);
    setSelectedTierId(tierId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    setError(null);

    if (!canSubmit) return;

    const selectedTier = subscriptionConfig?.plans?.[selectedPlanType]?.tiers?.find(
      (t) => t.id === selectedTierId
    );

    const payload: GroupeCreateInput = {
      name: name.trim(),
      groupe_type: groupeType,
      licence,
      plan_type: isPremium ? selectedPlanType : "BASIC",
      tier_id: isPremium ? selectedTierId : (firstBasicTier?.id ?? "basic-10"),
      max_parcours_per_month: isPremium
        ? (selectedTier?.maxTrees ?? 10)
        : (firstBasicTier?.maxTrees ?? 10),
      current_month_parcours: 0,
      is_init: false,
      is_stripped: false,
      subscription_status: "ACTIVE",
    } as unknown as GroupeCreateInput;

    try {
      const result = await createGroupe.mutateAsync(payload);
      const data = result.data as any;
      onGroupeCreated({
        id: data.id,
        name: data.name ?? name.trim(),
        groupe_type: data.groupe_type ?? groupeType,
        licence: data.licence ?? licence,
        plan_type: data.plan_type ?? (isPremium ? selectedPlanType : undefined),
        max_parcours_per_month: data.max_parcours_per_month,
        tier_id: data.tier_id ?? (isPremium ? selectedTierId : undefined),
      });
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          "Une erreur est survenue lors de la création du groupe."
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Section: Informations générales */}
      <div className="space-y-5">
        <div className="flex items-center gap-2 pb-1 border-b border-border/60">
          <Building2 className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Informations générales
          </h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Nom */}
          <div className="space-y-2">
            <Label htmlFor="group-name" className="text-sm font-medium">
              Nom du groupe{" "}
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="group-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (touched && e.target.value.trim()) setError(null);
              }}
              onBlur={() => setTouched(true)}
              placeholder="Ex : Mairie de Paris"
              className={cn(
                "h-10",
                nameError && "border-destructive focus-visible:ring-destructive/30"
              )}
            />
            {nameError && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Le nom du groupe est requis
              </p>
            )}
          </div>

          {/* Licences */}
          <div className="space-y-2">
            <Label htmlFor="group-licence" className="text-sm font-medium">
              <span className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                Nombre de licences{" "}
                <span className="text-destructive">*</span>
              </span>
            </Label>
            <Input
              id="group-licence"
              type="number"
              min={1}
              value={licence}
              onChange={(e) => setLicence(parseInt(e.target.value) || 1)}
              onBlur={() => setTouched(true)}
              className={cn(
                "h-10",
                licenceError && "border-destructive focus-visible:ring-destructive/30"
              )}
            />
            <p className="text-xs text-muted-foreground">
              Nombre d&apos;utilisateurs autorisés dans ce groupe
            </p>
            {licenceError && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Minimum 1 licence requise
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Section: Type de groupe */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-1 border-b border-border/60">
          <Package className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Type de groupe
          </h3>
        </div>

        <RadioGroup
          value={groupeType}
          onValueChange={(v) => {
            setGroupeType(v as GroupeType);
            // Auto-select first tier of new type
            if (v === "PREMIUM" && subscriptionConfig) {
              const tier = subscriptionConfig.plans?.BASIC?.tiers?.[0];
              if (tier) {
                setSelectedPlanType("BASIC");
                setSelectedTierId(tier.id);
              }
            }
          }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-3"
        >
          {/* DENDROMAP */}
          <div
            className={cn(
              "flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-all duration-150 active:scale-[0.99]",
              groupeType === "DENDROMAP"
                ? "border-primary bg-primary/5 shadow-sm"
                : "border-border/60 hover:border-border bg-card"
            )}
            onClick={() => setGroupeType("DENDROMAP")}
          >
            <RadioGroupItem value="DENDROMAP" id="type-dendromap" className="mt-0.5 shrink-0" />
            <Label
              htmlFor="type-dendromap"
              className="flex flex-col gap-1 cursor-pointer flex-1"
            >
              <span className="flex items-center gap-2 font-semibold text-sm">
                <Package className="h-4 w-4 text-primary" />
                Dendromap
              </span>
              <span className="text-xs text-muted-foreground leading-relaxed">
                Groupe interne — accès standard à la plateforme sans abonnement commercial.
              </span>
            </Label>
          </div>

          {/* PREMIUM */}
          <div
            className={cn(
              "flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-all duration-150 active:scale-[0.99]",
              groupeType === "PREMIUM"
                ? "border-amber-500 bg-amber-50/60 shadow-sm"
                : "border-border/60 hover:border-border bg-card"
            )}
            onClick={() => setGroupeType("PREMIUM")}
          >
            <RadioGroupItem value="PREMIUM" id="type-premium" className="mt-0.5 shrink-0" />
            <Label
              htmlFor="type-premium"
              className="flex flex-col gap-1 cursor-pointer flex-1"
            >
              <span className="flex items-center gap-2 font-semibold text-sm">
                <Crown className="h-4 w-4 text-amber-600" />
                Premium
                <span className="ml-auto text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full">
                  Client
                </span>
              </span>
              <span className="text-xs text-muted-foreground leading-relaxed">
                Groupe client avec abonnement — plan et forfait requis.
              </span>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Section: Plan — only for PREMIUM */}
      {isPremium && (
        <div className="space-y-4 animate-in fade-in-0 slide-in-from-top-2 duration-200">
          <div className="flex items-center gap-2 pb-1 border-b border-border/60">
            <Crown className="h-4 w-4 text-amber-600" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Plan d&apos;abonnement
            </h3>
            <span className="ml-auto text-xs text-destructive flex items-center gap-1">
              <Info className="h-3 w-3" />
              Requis pour PREMIUM
            </span>
          </div>

          {isLoadingConfig ? (
            <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Chargement des plans...
            </div>
          ) : subscriptionConfig ? (
            <GroupePlanSection
              subscriptionConfig={subscriptionConfig}
              selectedPlanType={selectedPlanType}
              selectedTierId={selectedTierId}
              onPlanSelect={handlePlanSelect}
            />
          ) : null}

          {planError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Veuillez sélectionner un plan d&apos;abonnement pour continuer.
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end pt-2">
        <Button
          type="submit"
          disabled={createGroupe.isPending}
          size="lg"
          className="min-w-[200px] gap-2"
        >
          {createGroupe.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Création en cours...
            </>
          ) : (
            <>
              Créer le groupe
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
